import React, { Fragment, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { GatewayDataContext } from "app/home/Home";
import { DeviceColors } from "app/model/Device";
import ScenarioSwitchIcon from "app/home/home-components/scenario-switch-icon/ScenarioSwitchIcon";
import { ScenarioCell, ScenarioList } from "app/home/main-pages/scenarios/Scenarios";
import { useLocalizedStrings } from "app/localization/Context";
import { ColorSelector } from "../DeviceDetail";

import { ReactComponent as IconDisconnect } from 'images/general/disconnect.svg';
import { ReactComponent as IconEdit } from 'images/profile/edit.svg';
import { ReactComponent as IconBack } from 'images/general/back.svg';
import { ReactComponent as IconTick } from 'images/general/tick.svg';


export function ScenarioSwitchControl({ device, setSectionEditShape }) {
    const s = useLocalizedStrings();

    return (
        <Fragment>
            {
                device.active ? null :
                    <div className="scenario-switch-dc">
                        <div className="label">{s.deviceDisonnected}</div>
                        <IconDisconnect className="icon" />
                    </div>
            }
            <ScenarioSection shape="o" device={device}
                onEditClick={() => setSectionEditShape("o")} />
            <ScenarioSection shape="x" device={device}
                onEditClick={() => setSectionEditShape("x")} />
            <ScenarioSection shape="t" device={device}
                onEditClick={() => setSectionEditShape("t")} />
            <ScenarioSection shape="r" device={device}
                onEditClick={() => setSectionEditShape("r")} />

            <div style={{ height: 16 }} /> {/* blank space */}
        </Fragment>
    );
}

export function ScenarioSection({ shape, device, onEditClick }) {
    const s = useLocalizedStrings();
    const gw = useContext(GatewayDataContext);

    const orderStr = { "o": 1, "x": 2, "t": 3, "r": 4 }[shape]
    const colorCode = device["color" + orderStr];
    const scenarioId = device["scenario" + orderStr];

    const scenarios = gw.gateway.scenarios;
    const scenario = useMemo(() => {
        return scenarios.find(({ id }) => id === scenarioId);
    }, [scenarios, scenarioId]);
    const isValid = (scenarioId !== -1) && (scenarioId !== undefined) && (scenario !== undefined);
    let color = "#595757";
    if (isValid && (colorCode !== undefined) && (colorCode !== (DeviceColors.length - 1))) {
        color = DeviceColors[colorCode][1];
    }

    return (
        <div className={device.active ? "scenario-section" : "scenario-section inactive"}>
            <ScenarioSwitchIcon color={color} shape={shape} className="icon" />
            <div className="title">{s.scenarioSwitchSectionTitle[shape]}</div>
            {
                isValid ?
                    <ScenarioCell
                        cantRun={!gw.gateway.currentUser.permissions.scenario.run}
                        scenario={scenario} scenarioReports={gw.gateway.scenarioReports} onDetailClickDisabled /> :
                    <div className="no-scenario">{s.scenarioSwitchSectionNotSpecified}</div>
            }
            {
                gw.gateway.currentUser.permissions.setting ? (
                    <div className="edit-border" onClick={onEditClick}>
                        <div className="label">{s.edit}</div>
                        <IconEdit className="icon" />
                    </div>
                ) : null
            }
        </div>
    );
}

export function EditScenarioSwitchPage({ scenarios, sectionEditShape, device, setSectionEditShape }) {
    const s = useLocalizedStrings();
    const ds = s.deviceSettings;

    const [shapeCache, setShapeCache] = useState(sectionEditShape);
    useEffect(() => {
        if (sectionEditShape !== undefined) {
            setShapeCache(sectionEditShape);
        }
    }, [sectionEditShape]);

    const dismiss = useCallback(() => {
        setSectionEditShape(undefined);
    }, [setSectionEditShape]);
    const orderStr = { "o": 1, "x": 2, "t": 3, "r": 4 }[shapeCache]
    const [colorCode, setColorCode] = useState(device["color" + orderStr]);
    const [scenarioId, setScenarioId] = useState(device["scenario" + orderStr]);

    const saveHandler = useCallback(() => {
        let changeStateObject = {};
        if ((scenarioId !== -1) && (scenarioId !== undefined) && (scenarioId !== null)) {
            changeStateObject["scenario" + orderStr] = scenarioId;
        } else {
            changeStateObject["scenario" + orderStr] = -1;
        }
        changeStateObject["color" + orderStr] = colorCode;
        device.changeState(changeStateObject);
        dismiss();
    }, [device, orderStr, colorCode, scenarioId, dismiss]);

    const selectionCallback = useCallback((_scenario) => {
        if (scenarioId === _scenario.id) {
            setScenarioId(-1);
        } else {
            setScenarioId(_scenario.id);
        }
    }, [scenarioId]);

    return (
        <div className="scenario-switch-edit-page">
            <div className="bg-blank" />
            <div className="bg-secondary-color" />
            <div className="scenario-switch-edit-page-content">
                <IconBack className="nav-icon" />
                <div className="nav-button" onClick={dismiss} />
                <div className="page-header with-margin-bottom">{s.scenarioSwitchSectionTitle[shapeCache]}</div>

                <div className="section-header">{ds.color}</div>
                <ColorSelector selectedColorCode={colorCode} setColorCode={setColorCode} />

                <div className="section-header">{s.scenarioSwitchAssociatedScenario}</div>
                <ScenarioList
                    // bottomPaddingSize={64 + 16 + 8} // +8 for safe-area
                    marginRight={0} marginLeft={0}
                    scenarios={scenarios}
                    selectionCallback={selectionCallback}
                    selectedScenarios={
                        (scenarioId !== -1) && (scenarioId !== undefined) && (scenarioId !== null) ?
                            [scenarioId] :
                            []} />

                <div style={{ height: 64 + 16 }} /> {/* blank space */}

                {/* vvvv in CreateScenario.scss vvvv */}
                <div className="full-blue-button next-button" onClick={saveHandler}>
                    <div>{s.save}</div> <IconTick className="icon" />
                </div>
            </div>
        </div>
    );
}